import {useEffect, useMemo, useRef, useState} from 'react';
import {useMutation} from '@apollo/client';
import {
  cn,
  MOBILE_SHEET_EASE_IN,
  CanvasRenderer,
  CanvasHeader,
  CanvasNavbar,
  buildCanvasCssVars,
  resolveCanvasBrand,
  canvasAuthorAdvisor,
} from '@continuum/ui';
import {LoadingScreen} from '~/components/loading-screen';
import type {
  CanvasActionHandlers,
  CanvasBrandColors,
  CanvasFooterCompliance,
} from '@continuum/ui';
import {
  CanvasIframe,
  type CanvasChrome,
  type CanvasIframeHandle,
  type CanvasImageSource,
} from '@continuum/canvas-runtime';
import type {Canvas as CanvasType} from '~/domains/canvas';
import {
  mapSharingCanvasToView,
  SharingCanvasBridge,
  useCanvasPrint,
} from '~/domains/canvas';
import {
  CanvasRegisterViewMutation,
  CanvasDocumentDownloadMutation,
} from '~/domains/canvas/graphql';
import {useAuth, useOrganizationIdentifier} from '~/lib/auth';
import {addUrlSchemeIfNecessary, triggerBrowserDownload} from '~/lib/utils';
import {
  PreviewBanner,
  CanvasPanel,
  CanvasPanelDialog,
  ChatFab,
  ChatPanelContent,
  PagesProvider,
  PagesView,
} from './components';
import {useCanvasChat} from './hooks/use-canvas-chat';
import {useCanvasPanel} from './hooks/use-canvas-panel';
import {usePagesHeaderEnabled} from './hooks/use-pages-header-enabled';

function publicImageSource(
  url: string | null | undefined,
): CanvasImageSource | null {
  return url ? {sourceId: url, url} : null;
}

interface Props {
  canvas: CanvasType;
  viewerEmail: string | null;
}

export const Canvas = ({canvas, viewerEmail}: Props) => {
  const {isPreview, isAnonymous} = useAuth();
  const organizationIdentifier = useOrganizationIdentifier();
  const pagesHeaderEnabled = usePagesHeaderEnabled();
  const [registerView] = useMutation(CanvasRegisterViewMutation);
  const [downloadDocument] = useMutation(CanvasDocumentDownloadMutation);
  const isV2 = canvas.architecture === 'V2_CODE';
  const {
    activePanel,
    visiblePanel,
    isPanelOpen,
    isDesktop,
    isLargeDesktop,
    setActivePanel,
    closePanel,
  } = useCanvasPanel();
  const chat = useCanvasChat();

  useEffect(() => {
    if (isPreview) {
      return;
    }
    registerView({variables: {canvasId: canvas.id}}).catch(() => {
      // Fire-and-forget, no UI impact
    });
  }, [canvas.id, isPreview, registerView]);

  const canvasData = useMemo(() => mapSharingCanvasToView(canvas), [canvas]);

  const brand = useMemo<CanvasBrandColors>(() => {
    const bp = canvas.organization.brandProfile;
    return resolveCanvasBrand(
      bp
        ? {
            primary: bp.primaryColor,
            accent: bp.accentColor,
            fontStyle: bp.fontStyle,
            cornerStyle: bp.cornerStyle,
          }
        : null,
    );
  }, [canvas.organization.brandProfile]);

  const cssVars = useMemo(() => buildCanvasCssVars(brand), [brand]);

  const compliance = useMemo<CanvasFooterCompliance | null>(() => {
    const profile = canvas.organization.complianceProfile;
    if (!profile) {
      return null;
    }
    return {
      disclosure: profile.disclosure,
      dealerEnabled: profile.dealerEnabled,
      dealerName: profile.dealerName,
      dealerLogoUrl: profile.dealerLogoUrl,
      companyName: profile.companyName,
      companyLogoUrl: profile.companyLogoUrl,
    };
  }, [canvas.organization.complianceProfile]);

  const actions: CanvasActionHandlers = useMemo(
    () => ({
      onDocumentDownload: async ({documentId, documentName}) => {
        try {
          const result = await downloadDocument({
            variables: {canvasDocumentId: documentId},
          });
          const url = result.data?.canvasDocumentDownload?.downloadUrl;
          if (url) {
            triggerBrowserDownload(url, documentName);
          }
        } catch {
          // Mutation errors are surfaced by Apollo's global error handler
        }
      },
      onScheduleClick: ({url}) => {
        if (url?.trim()) {
          const normalizedUrl = addUrlSchemeIfNecessary(url.trim());
          window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
        } else if (canvasData.authorEmail) {
          window.location.href = `mailto:${canvasData.authorEmail}`;
        }
      },
    }),
    [downloadDocument, canvasData.authorEmail],
  );

  const v2Bridge = useMemo(() => {
    if (!isV2) return null;

    const advisorAuthor = canvasAuthorAdvisor(canvasData);
    const chrome: CanvasChrome = {
      brand: {...brand, companyName: canvas.organization.name},
      brandLogo: publicImageSource(canvas.organization.logoUrl),
      compliance: compliance
        ? {
            disclosure: compliance.disclosure,
            dealerEnabled: compliance.dealerEnabled,
            dealerName: compliance.dealerName,
            dealerLogo: publicImageSource(compliance.dealerLogoUrl),
            companyName: compliance.companyName,
            companyLogo: publicImageSource(compliance.companyLogoUrl),
          }
        : null,
      advisor: {
        name: advisorAuthor?.name,
        email: advisorAuthor?.email,
        avatar: publicImageSource(advisorAuthor?.avatarUrl),
      },
    };

    return new SharingCanvasBridge({
      sourceCode: canvas.sourceCode ?? '',
      entities: canvas.entities,
      chrome,
      downloadDocument: async (canvasDocumentId, filename) => {
        const result = await downloadDocument({
          variables: {canvasDocumentId},
        });
        const url = result.data?.canvasDocumentDownload?.downloadUrl;
        if (!url) {
          throw new Error('download URL missing from response');
        }
        triggerBrowserDownload(url, filename);
      },
    });
  }, [
    isV2,
    canvas.sourceCode,
    canvas.entities,
    canvas.organization,
    brand,
    canvasData,
    compliance,
    downloadDocument,
  ]);

  const [hasFirstRendered, setHasFirstRendered] = useState(false);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const iframeHandleRef = useRef<CanvasIframeHandle>(null);

  const {onPrintShortcut} = useCanvasPrint({
    iframeRef: iframeHandleRef,
    enabled: isV2,
  });

  // v1 is deprecated, and preview/identifier-less tokens can't load a list.
  // Anonymous visitors have no grants, and the viewer API refuses them.
  const showPagesView =
    pagesHeaderEnabled &&
    isV2 &&
    !isPreview &&
    !isAnonymous &&
    organizationIdentifier !== null;

  const panelView =
    visiblePanel === 'pages' ? (
      <PagesView onClose={closePanel} />
    ) : (
      <ChatPanelContent chat={chat} canvas={canvas} onClose={closePanel} />
    );

  // Flag on: the bar renders only when it has something to show. Flag off:
  // only v1 has a bar (CanvasNavbar); v2 runs edge to edge.
  const showHeader =
    pagesHeaderEnabled && (showPagesView || viewerEmail !== null);
  const hasHeaderBar = showHeader || (!pagesHeaderEnabled && !isV2);
  const panelOffset = cn({
    'top-0': !hasHeaderBar && !isPreview,
    'top-8': !hasHeaderBar && isPreview,
    'top-16': hasHeaderBar && !isPreview,
    'top-24': hasHeaderBar && isPreview,
  });

  useEffect(() => {
    setHasFirstRendered(false);
    setRuntimeError(null);
    if (!v2Bridge) {
      return;
    }
    v2Bridge.onRendered = () => {
      setHasFirstRendered(true);
      setRuntimeError(null);
    };
    v2Bridge.onRuntimeError = (message) => {
      setRuntimeError(message);
    };
    return () => {
      v2Bridge.onRendered = undefined;
      v2Bridge.onRuntimeError = undefined;
    };
  }, [v2Bridge]);

  const page = (
    <div
      className="bg-accent [--canvas-panel-width:clamp(366px,28vw,520px)]"
      style={cssVars}
    >
      <div className="sticky top-0 z-50 print:hidden">
        {isPreview && <PreviewBanner />}
        {pagesHeaderEnabled
          ? showHeader && (
              <CanvasHeader
                pages={
                  showPagesView
                    ? {
                        open: activePanel === 'pages',
                        onToggle: () =>
                          setActivePanel(
                            activePanel === 'pages' ? null : 'pages',
                          ),
                      }
                    : 'hidden'
                }
                viewer={viewerEmail ? {email: viewerEmail} : null}
              />
            )
          : !isV2 && <CanvasNavbar canvas={canvasData} />}
      </div>

      <div
        className={cn(
          'relative transition-[margin-right] duration-300 ease-in-out print:mr-0',
          isLargeDesktop && isPanelOpen && 'mr-(--canvas-panel-width)',
        )}
      >
        {v2Bridge ? (
          <div className="relative">
            <CanvasIframe
              ref={iframeHandleRef}
              adapter={v2Bridge}
              autoHeight
              onPrintShortcut={onPrintShortcut}
            />
            {!hasFirstRendered && (
              <div className="absolute inset-0 bg-background">
                {runtimeError ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="flex max-w-md flex-col items-center gap-3 px-6 text-center">
                      <p className="text-base font-medium text-foreground">
                        This page couldn&rsquo;t be displayed
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Something went wrong while loading this page. The author
                        has been notified.
                      </p>
                    </div>
                  </div>
                ) : (
                  <LoadingScreen />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="container max-w-2xl px-4 py-8">
            <CanvasRenderer
              canvas={canvasData}
              actions={actions}
              brand={brand}
              advisor={canvasAuthorAdvisor(canvasData)}
              compliance={compliance}
            />
          </div>
        )}

        {!isDesktop && (
          <div
            className={cn(
              'absolute inset-0 bg-background transition-opacity duration-300 print:hidden',
              MOBILE_SHEET_EASE_IN,
              isPanelOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          />
        )}
      </div>

      {isDesktop ? (
        <CanvasPanel
          open={isPanelOpen}
          variant={isLargeDesktop ? 'flush' : 'floating'}
          className={cn('print:hidden', panelOffset)}
        >
          {panelView}
        </CanvasPanel>
      ) : (
        <CanvasPanelDialog
          open={isPanelOpen}
          onOpenChange={(next) => !next && closePanel()}
          title={
            visiblePanel === 'pages' ? 'Your pages' : 'Ask about this page'
          }
          cssVars={cssVars}
        >
          {panelView}
        </CanvasPanelDialog>
      )}

      <ChatFab
        onClick={() => setActivePanel('chat')}
        visible={activePanel !== 'chat'}
        shifted={isDesktop && isPanelOpen}
        className="print:hidden"
      />
    </div>
  );

  // The flag is final before Canvas mounts (see SharedEntity), so this wrapper
  // never swaps mid-session. Without it no viewer client or session exists.
  return showPagesView ? <PagesProvider>{page}</PagesProvider> : page;
};
